import './FellowshipEggCorner.css';

import { useState, useEffect } from 'react';
import eggCarton from '../assets/images/egg_carton.png';
import eggQuestion from '../assets/images/egg_question.png';
import hobbitLora from '../assets/images/hobbitlora.gif';
import { IMAGE_MAP, URL } from './constants';

/**
 * @param {{ nickname: string, image: string }[]} [eggableUsers]
 */
export default function FellowshipEggCorner({ eggableUsers = [] }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [eggSubmitting, setEggSubmitting] = useState(false);
  const [eggError, setEggError] = useState('');

  const n = eggableUsers.length;
  const safeIndex = n === 0 ? 0 : ((avatarIndex % n) + n) % n;
  const current = eggableUsers[safeIndex];
  const imageUrl = current ? IMAGE_MAP[current.image] || hobbitLora : null;

  useEffect(() => {
    if (!popupOpen) return;
    setAvatarIndex(0);
    setEggError('');
  }, [popupOpen]);

  useEffect(() => {
    if (n === 0) {
      setAvatarIndex(0);
      return;
    }
    setAvatarIndex((i) => Math.min(i, n - 1));
  }, [n]);

  useEffect(() => {
    setEggError('');
  }, [safeIndex]);

  useEffect(() => {
    if (!popupOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setPopupOpen(false);
      if (n <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setAvatarIndex((i) => (i - 1 + n) % n);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setAvatarIndex((i) => (i + 1) % n);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupOpen, n]);

  const goPrev = () => {
    if (n <= 1) return;
    setAvatarIndex((i) => (i - 1 + n) % n);
  };

  const goNext = () => {
    if (n <= 1) return;
    setAvatarIndex((i) => (i + 1) % n);
  };

  const handleEggSend = async () => {
    if (!current?.nickname || eggSubmitting) return;
    setEggError('');
    setEggSubmitting(true);
    try {
      const res = await fetch(`${URL}/egg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ egged: current.nickname }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* empty or non-JSON */
      }
      if (!res.ok) {

        let msg = data.error 
        if (data.code === 'one_egg_per_day') {
          msg = 'Your greed sickens me. Only one egg per day.'
        }
        throw new Error(msg);
      }
      setPopupOpen(false);
    } catch (err) {
      setEggError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setEggSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="fellowship-egg-corner"
        onClick={() => setPopupOpen(true)}
        aria-label="Open egg question"
      >
        <img src={eggCarton} alt="" />
      </button>
      {popupOpen && (
        <div
          className="fellowship-egg-overlay"
          role="presentation"
          onClick={() => setPopupOpen(false)}
        >
          <div
            className="fellowship-egg-popup"
            role="dialog"
            aria-modal="true"
            aria-label="Egg question"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="fellowship-egg-popup-close"
              onClick={() => setPopupOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="fellowship-egg-stage">
              <img src={eggQuestion} alt="" className="fellowship-egg-popup-img" />
              {imageUrl && (
                <button
                  type="button"
                  className="fellowship-egg-avatar-slot"
                  onClick={handleEggSend}
                  disabled={eggSubmitting}
                  aria-label={`Egg ${current.nickname}`}
                >
                  <img
                    src={imageUrl}
                    alt=""
                    className="fellowship-egg-avatar"
                    title={current.nickname}
                    draggable={false}
                  />
                </button>
              )}
              {n > 0 && current && (
                <div className="fellowship-egg-title-wrap">
                  <button
                    type="button"
                    className="fellowship-egg-title-btn"
                    onClick={handleEggSend}
                    disabled={eggSubmitting}
                    aria-label={`Egg ${current.nickname}`}
                  >
                    <span className="fellowship-egg-title-label fellowship-egg-title-label--rest">
                      Egg {current.nickname}?
                    </span>
                    <span className="fellowship-egg-title-label fellowship-egg-title-label--hover">
                      egg!
                    </span>
                  </button>
                  {eggError ? (
                    <p className="fellowship-egg-api-error" role="alert">
                      {eggError}
                    </p>
                  ) : null}
                </div>
              )}
              <button
                type="button"
                className="fellowship-egg-caret fellowship-egg-caret-left"
                onClick={goPrev}
                disabled={n <= 1}
                aria-label="Previous eggable user"
              >
                <span className="fellowship-egg-caret-glyph">‹</span>
              </button>
              <button
                type="button"
                className="fellowship-egg-caret fellowship-egg-caret-right"
                onClick={goNext}
                disabled={n <= 1}
                aria-label="Next eggable user"
              >
                <span className="fellowship-egg-caret-glyph">›</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
